import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link } from '@tanstack/react-router';
import { Clock } from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import { useSettingsStore } from '@/stores/settings.store';
import { daysSince } from '@trinco/shared';
import type { Deal } from '@trinco/shared';

interface DealCardProps {
  deal: Deal;
  isDragOverlay?: boolean;
}

export function DealCard({ deal, isDragOverlay }: DealCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: deal.id,
  });

  const style = isDragOverlay
    ? undefined
    : { transform: CSS.Transform.toString(transform), transition };

  const baseCurrency = useSettingsStore((s) => s.baseCurrency);
  const daysInStage = daysSince(deal.updatedAt);
  const isStale = daysInStage > 14;
  const isVeryStale = daysInStage > 30;

  const card = (
    <div
      ref={!isDragOverlay ? setNodeRef : undefined}
      style={style}
      {...(!isDragOverlay ? { ...attributes, ...listeners } : {})}
      className={cn(
        'rounded-lg border bg-card p-3 shadow-sm transition-shadow hover:shadow-md cursor-grab',
        isDragging && 'opacity-50',
        isDragOverlay && 'shadow-lg rotate-2',
        isVeryStale && 'border-red-300 dark:border-red-800',
        isStale && !isVeryStale && 'border-amber-300 dark:border-amber-800',
      )}
    >
      <div className="space-y-2">
        <div>
          <p className="font-medium text-sm leading-tight">{deal.title}</p>
          {deal.companyName && (
            <p className="text-xs text-muted-foreground">{deal.companyName}</p>
          )}
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">
            {deal.amount ? (
              <span className="flex flex-col items-start">
                <span>{formatCurrency(deal.amountCad ?? deal.amount, baseCurrency)}</span>
                {deal.currency && deal.currency !== baseCurrency && (
                  <span className="text-xs font-normal text-muted-foreground">
                    {formatCurrency(deal.amount, deal.currency)}
                  </span>
                )}
              </span>
            ) : '-'}
          </span>

          <div
            className={cn(
              'flex items-center gap-1 text-xs',
              isVeryStale
                ? 'text-red-500'
                : isStale
                  ? 'text-amber-500'
                  : 'text-muted-foreground',
            )}
          >
            <Clock className="h-3 w-3" />
            <span>{daysInStage}d</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (isDragOverlay) return card;

  return (
    <Link to="/deals/$dealId" params={{ dealId: deal.id }}>
      {card}
    </Link>
  );
}
